import { FC } from 'react';
import { Table, Collapse } from 'antd';
import { CodeOutlined, WarningOutlined } from '@ant-design/icons';
import { useIntl, FormattedMessage } from 'react-intl';

import { useFelaEnhanced } from 'hooks';

import * as felaRules from './SqlResultTable.rules';
import { SqlResult } from '../../types';

interface SqlResultTableProps {
    result: SqlResult;
}

const formatCell = (value: unknown) => {
    if (value === null || value === undefined) {
        return '—';
    }
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }
    return String(value);
};

const SqlResultTable: FC<SqlResultTableProps> = ({ result }) => {
    const { styles } = useFelaEnhanced(felaRules);
    const intl = useIntl();

    const columns = result.columns.map((column, index) => ({
        title: column,
        dataIndex: String(index),
        key: column,
        render: formatCell,
    }));

    const dataSource = result.rows.map((row, rowIndex) => {
        const record: Record<string, unknown> = { __rowKey: rowIndex };
        row.forEach((value, index) => {
            record[String(index)] = value;
        });
        return record;
    });

    return (
        <div className={styles.container}>
            <div className={styles.meta}>
                <FormattedMessage id="assistant.sql.meta" values={{ rowCount: result.rowCount }} />
                {result.truncated && (
                    <span className={styles.truncated}>
                        <WarningOutlined style={{ marginRight: 4 }} />
                        <FormattedMessage id="assistant.sql.truncated" values={{ rowCount: result.rowCount }} />
                    </span>
                )}
            </div>
            <Table
                className={styles.table}
                columns={columns}
                dataSource={dataSource}
                rowKey="__rowKey"
                pagination={false}
                size="small"
                scroll={{ x: 'max-content' }}
            />
            <Collapse className={styles.sqlDisclosure} ghost>
                <Collapse.Panel
                    key="sql"
                    header={
                        <span>
                            <CodeOutlined style={{ marginRight: 6 }} />
                            {intl.formatMessage({ id: 'assistant.sql.viewGenerated' })}
                        </span>
                    }
                >
                    <pre className={styles.sqlBlock}>{result.sql}</pre>
                </Collapse.Panel>
            </Collapse>
        </div>
    );
};

export default SqlResultTable;
